import { Controller } from "@hotwired/stimulus"

// Uploads several documents at once.
//
// Each file is posted on its own as soon as it is chosen, and the form itself
// then carries only the returned blob ids and the type picked for each file.
// Twenty documents are therefore twenty short requests rather than one
// multipart body that would outlive the request timeout.
export default class extends Controller {
  static targets = [
    "input", "dropZone", "table", "rows", "rowTemplate", "defaultType",
    "submit", "pendingNote", "untypedNote"
  ]
  static values = { uploadUrl: String }

  connect() {
    this.pending = 0
    this.refresh()
  }

  browse(event) {
    // The hidden input is inside the drop zone, so its own click would come
    // back here and reopen the picker.
    if (event.target !== this.inputTarget) this.inputTarget.click()
  }

  onFilesChosen() {
    this.addFiles(this.inputTarget.files)
    // Cleared so choosing the same file again still fires a change event.
    this.inputTarget.value = ""
  }

  onDragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("bg-light")
  }

  onDragLeave() {
    this.dropZoneTarget.classList.remove("bg-light")
  }

  onDrop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("bg-light")
    this.addFiles(event.dataTransfer.files)
  }

  addFiles(files) {
    Array.from(files).forEach(file => this.addFile(file))
    this.refresh()
  }

  addFile(file) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    this.field(row, "fileName").textContent = file.name
    this.field(row, "name").value = file.name
    // Applied as the row is created rather than over the whole table, so
    // adding more files never overwrites a type already corrected by hand.
    this.field(row, "kind").value = this.defaultTypeTarget.value
    this.rowsTarget.appendChild(row)
    this.upload(file, row)
  }

  // XMLHttpRequest rather than fetch, because it is the only one that reports
  // how much of the file has gone out.
  upload(file, row) {
    this.pending += 1
    this.refresh()

    const body = new FormData()
    body.append("file", file)

    const request = new XMLHttpRequest()
    request.open("POST", this.uploadUrlValue, true)
    request.setRequestHeader("Accept", "application/json")
    request.setRequestHeader("X-CSRF-Token", this.csrfToken())

    request.upload.addEventListener("progress", event => {
      if (event.lengthComputable) this.setProgress(row, (event.loaded / event.total) * 100)
    })

    request.addEventListener("load", () => {
      this.pending -= 1

      if (request.status >= 200 && request.status < 300) {
        this.field(row, "signedId").value = JSON.parse(request.responseText).signed_id
        this.setProgress(row, 100)
      } else {
        this.showRowError(row, this.errorFrom(request))
      }

      this.refresh()
    })

    request.addEventListener("error", () => {
      this.pending -= 1
      this.showRowError(row, "Upload failed. Remove this row and try again.")
      this.refresh()
    })

    request.send(body)
  }

  // The server says why when it can -- too large, for instance -- and that is
  // more use than a generic failure.
  errorFrom(request) {
    try {
      return JSON.parse(request.responseText).error || "Upload failed. Remove this row and try again."
    } catch (e) {
      return "Upload failed. Remove this row and try again."
    }
  }

  csrfToken() {
    const tag = document.querySelector("meta[name=csrf-token]")
    return tag ? tag.content : ""
  }

  removeRow(event) {
    event.target.closest("tr").remove()
    this.refresh()
  }

  onSubmit(event) {
    if (!this.readyToSave()) {
      event.preventDefault()
      return
    }

    // The chooser is emptied after every selection, but disable it so the
    // files themselves can never ride along with the ids.
    this.inputTarget.disabled = true

    // Named only now, and only for rows that actually uploaded, so a failed
    // or removed file cannot reach the server as a blank document.
    this.rowElements()
      .filter(row => this.field(row, "signedId").value)
      .forEach((row, index) => {
        ["signedId", "name", "kind"].forEach(field => {
          const input = this.field(row, field)
          input.disabled = false
          input.name = `documents[${index}][${this.paramFor(field)}]`
        })
      })
  }

  refresh() {
    const rows = this.rowElements()
    const uploaded = rows.filter(row => this.field(row, "signedId").value)

    this.tableTarget.classList.toggle("d-none", rows.length === 0)
    this.pendingNoteTarget.classList.toggle("d-none", this.pending === 0)
    this.untypedNoteTarget.classList.toggle("d-none", this.pending > 0 || uploaded.length === 0 || this.readyToSave())
    this.submitTarget.disabled = !this.readyToSave()
  }

  readyToSave() {
    const rows = this.rowElements()
    const uploaded = rows.filter(row => this.field(row, "signedId").value)

    return this.pending === 0 &&
      uploaded.length > 0 &&
      uploaded.every(row => this.field(row, "kind").value)
  }

  rowElements() {
    return Array.from(this.rowsTarget.children)
  }

  field(row, name) {
    return row.querySelector(`[data-row-field="${name}"]`)
  }

  paramFor(field) {
    return field === "signedId" ? "signed_id" : field
  }

  setProgress(row, percent) {
    this.field(row, "progressBar").style.width = `${percent}%`
  }

  showRowError(row, message) {
    const error = this.field(row, "rowError")
    error.textContent = message
    error.classList.remove("d-none")
  }
}
